import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import SupportersLabel from '@wix/da-ds/pkg/Label/Supporters';
import AdoptableCountdown from '@wix/da-ds/pkg/Time/AdoptableCountdown';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import CashbackBanner from '../../../../../../banners/CashbackBanner';
import useAdoptableState, {
  UseAdoptableStateReturnType,
} from '../../../AdoptableInfo/useAdoptableState';
import Card from '../../lego/Card';
import CardHeading from '../../lego/CardHeading';
import Description from '../../lego/Description';
import BuyerButtons from './BuyerButtons';
import SellerButtons from './SellerButtons';
import Countdown from '../../lego/Countdown';
import MinimumOffer from '../../lego/MinimumOffer';
import MinOfferAndCountdown from '../../lego/MinOfferAndCountdown';
import OfferList from '../../lego/OfferList';
import Stack from '../../lego/Stack';
import TradeCount from '../../lego/TradeCount';
import ResaleIndicator from '../../lego/ResaleIndicator';
import { useExclusiveCategoryTranslation } from '../../hooks';

import s from './AdoptableForSale.scss';

export interface Props {
  deviation: PapiDeviation;
  className?: string;
}

function getDescriptionTranslation(
  t: TFunction,
  { isSeller, canAdopt, earlyAccess }: UseAdoptableStateReturnType
) {
  if (isSeller) {
    return t('sidebar.exclusives.description.seller');
  } else if (canAdopt) {
    return t('sidebar.exclusives.description.buyer');
  } else if (earlyAccess.isInEarlyAccess && !earlyAccess.isSupporter) {
    return t('sidebar.exclusives.description.earlyAccess.notSupporter');
  } else {
    return t('sidebar.exclusives.description.other');
  }
}

/**
 * An adoptable specific variant of our exclusive sidebar card. Used only when the exclusive is currently listed for sale.
 *
 * NOTE: This component is used for all exclusive types as of this comment's writing. There is no difference spec'd between
 * these cards. Because I don't believe this will stay the case, I architected this in a way to make it easy to make a brand-new
 * sidebar card component.
 *
 * If an exclusive category's card features start to diverge, make a new component instead of putting it in here.
 */
const AdoptableForSale: React.FC<Props> = ({ deviation, className }) => {
  const { t } = useTranslation();
  const exclusiveCategoryName = useExclusiveCategoryTranslation(deviation);
  const adoptableState = useAdoptableState(deviation);
  const {
    isSeller,
    canBuyNow,
    canMakeNewOffer,
    canManageExistingOffer,
    canSeeMinOfferPrice,
    canSeeOfferCountdown,
    hasOffers,
    offersList,
    minOfferPrice,
    saleCount,
    offerCountdownEndTime,
    offerCountdownHasEnded,
    onOfferTimerEnd,
    canSeeEarlyAccessCountdown,
    earlyAccessCountdownHasEnded,
    earlyAccessCountdownEndTime,
    onEarlyAccessTimerEnd,
    earlyAccess,
  } = adoptableState;

  const cashback = useFlag('cashback');

  return (
    <>
      <Card badgeType="adoptable" className={className}>
        <Stack gap={16}>
          <CardHeading deviation={deviation}>{exclusiveCategoryName}</CardHeading>

          {(canBuyNow || canMakeNewOffer || canManageExistingOffer || isSeller) && (
            <Description>{getDescriptionTranslation(t, adoptableState)}</Description>
          )}

          {!!saleCount && (
            <TradeCount
              className={s['trade-count']}
              deviation={deviation}
              withFlames={true}
              count={saleCount}
            />
          )}

          {!saleCount && (
            <ResaleIndicator isResellable={!!deviation.extended?.adoptable?.resaleAllowed} />
          )}

          {isSeller &&
            canSeeEarlyAccessCountdown &&
            // Just checking earlyAccessCountdownEndTime for typescript's sake. canSeeEarlyAccessCountdown should cover it.
            earlyAccessCountdownEndTime && (
              <p className={s['early-access-supporter-callout']}>
                {t(
                  'sidebar.exclusives.descriptionExtra.earlyAccess.isSupporter.withEarlyAccessTimer',
                  {
                    postProcess: ['reactPostprocessor', 'featureBrancher'],
                    logo: <SupportersLabel className={s['supporters-label']} withLink />,
                    timer: (
                      <AdoptableCountdown
                        className={s['inline-countdown']}
                        countToDate={earlyAccessCountdownEndTime}
                        endedClassName={s['ended']}
                        endedMessage={`00:00:00`}
                        onEnd={onEarlyAccessTimerEnd}
                      />
                    ),
                  }
                )}
              </p>
            )}

          {isSeller ? (
            <SellerButtons deviation={deviation} adoptableState={adoptableState} />
          ) : (
            <BuyerButtons deviation={deviation} adoptableState={adoptableState} />
          )}

          {earlyAccess.isInEarlyAccess &&
            !isSeller &&
            !canSeeOfferCountdown &&
            earlyAccessCountdownEndTime && (
              <p className={s['early-access-supporter-callout']}>
                {t(
                  'sidebar.exclusives.descriptionExtra.earlyAccess.isSupporter.withEarlyAccessTimer',
                  {
                    postProcess: ['reactPostprocessor'],
                    logo: <SupportersLabel className={s['supporters-label']} withLink />,
                    timer: (
                      <AdoptableCountdown
                        className={s['inline-countdown']}
                        countToDate={earlyAccessCountdownEndTime}
                        endedClassName={s['ended']}
                        endedMessage={`00:00:00`}
                        onEnd={onEarlyAccessTimerEnd}
                      />
                    ),
                  }
                )}
              </p>
            )}

          {/* if both countdown and min price should be shown */}
          {(canSeeOfferCountdown || canSeeEarlyAccessCountdown) &&
            offerCountdownEndTime !== undefined &&
            canSeeMinOfferPrice &&
            minOfferPrice > 0 && <MinOfferAndCountdown adoptableState={adoptableState} />}

          {/* if only min price is shown */}
          {!canSeeOfferCountdown && !canSeeEarlyAccessCountdown && canSeeMinOfferPrice && (
            <MinimumOffer value={minOfferPrice} />
          )}

          {/* if only countdown is shown */}
          <Stack gap={8}>
            {canSeeOfferCountdown &&
              !canSeeMinOfferPrice &&
              offerCountdownEndTime !== undefined && (
                <Countdown
                  hasEnded={offerCountdownHasEnded}
                  countToDate={offerCountdownEndTime}
                  onEnd={onOfferTimerEnd}
                />
              )}
            {!isSeller &&
              !canSeeOfferCountdown &&
              !canSeeMinOfferPrice &&
              canSeeEarlyAccessCountdown &&
              // Just checking earlyAccessCountdownEndTime for typescript's sake. canSeeEarlyAccessCountdown should cover it.
              earlyAccessCountdownEndTime && (
                <Countdown
                  isEarlyAccess
                  hasEnded={earlyAccessCountdownHasEnded}
                  countToDate={earlyAccessCountdownEndTime}
                  onEnd={onEarlyAccessTimerEnd}
                />
              )}
            {hasOffers && <OfferList deviation={deviation} offers={offersList} />}
          </Stack>
        </Stack>
      </Card>
      {cashback && <CashbackBanner narrow className={s['cashback-banner']} />}
    </>
  );
};

AdoptableForSale.displayName = 'AdoptableForSale';

export default AdoptableForSale;
